import React, { useEffect, useState } from "react";
import { supabase } from "../Services/supabaseClient";
import { Upload, Trash2, Paperclip } from "lucide-react";
import "../Product/customers.css";

export default function TransactionAttachments({
  companyId,
  entityType,
  entityId,
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [files, setFiles] = useState([]);

  async function fetchFiles() {
    if (!companyId || !entityType || !entityId) return;
    const { data, error } = await supabase
      .from("transaction_attachments")
      .select("*")
      .eq("company_id", companyId)
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .order("created_at", { ascending: false });

    if (!error) setFiles(data || []);
  }

  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, entityType, entityId]);

  async function upload(e) {
    setErr("");
    const file = e.target.files?.[0];
    if (!file) return;

    setBusy(true);
    try {
      const path = `${companyId}/${entityType}/${entityId}/${Date.now()}-${
        file.name
      }`;

      const { error: upErr } = await supabase.storage
        .from("transaction_attachments")
        .upload(path, file, { contentType: file.type, upsert: false });

      if (upErr) throw upErr;

      const { error: insErr } = await supabase
        .from("transaction_attachments")
        .insert({
          company_id: companyId,
          entity_type: entityType,
          entity_id: entityId,
          file_name: file.name,
          storage_path: path,
          mime_type: file.type,
          size_bytes: file.size,
        });

      if (insErr) throw insErr;

      await fetchFiles();
    } catch (e2) {
      setErr(e2.message || "Upload failed.");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  }

  async function openFile(row) {
    setErr("");
    try {
      const { data, error } = await supabase.storage
        .from("transaction_attachments")
        .createSignedUrl(row.storage_path, 60); // 60s

      if (error) throw error;
      window.open(data.signedUrl, "_blank");
    } catch (e) {
      setErr(e.message || "Failed to open file.");
    }
  }

  async function remove(row) {
    if (!window.confirm("Delete this attachment?")) return;
    setErr("");
    setBusy(true);
    try {
      await supabase.storage
        .from("transaction_attachments")
        .remove([row.storage_path]);
      await supabase.from("transaction_attachments").delete().eq("id", row.id);
      await fetchFiles();
    } catch (e) {
      setErr(e.message || "Delete failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="panel" style={{ padding: 14, marginTop: 12 }}>
      <div
        style={{
          fontWeight: 900,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Paperclip size={16} /> Attachments
      </div>

      <div
        style={{
          marginTop: 10,
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <label
          className="btn"
          style={{
            display: "inline-flex",
            gap: 8,
            alignItems: "center",
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          <Upload size={16} />
          {busy ? "Uploading…" : "Upload file"}
          <input
            type="file"
            onChange={upload}
            disabled={busy}
            style={{ display: "none" }}
          />
        </label>
        <div className="muted" style={{ fontSize: 12 }}>
          PDF, images, docs…
        </div>
      </div>

      {err && (
        <div className="banner error" style={{ marginTop: 10 }}>
          {err}
        </div>
      )}

      <div style={{ marginTop: 10 }}>
        {!files.length ? (
          <div className="muted">No attachments yet.</div>
        ) : (
          <table className="customer-table">
            <thead>
              <tr>
                <th>File</th>
                <th className="num">Size</th>
                <th className="center">Action</th>
              </tr>
            </thead>
            <tbody>
              {files.map((f) => (
                <tr key={f.id}>
                  <td>
                    <button className="btn-ghost" onClick={() => openFile(f)}>
                      {f.file_name}
                    </button>
                  </td>
                  <td className="num">
                    {Math.round((Number(f.size_bytes || 0) / 1024) * 10) / 10}{" "}
                    KB
                  </td>
                  <td className="center">
                    <button
                      className="icon-btn danger"
                      onClick={() => remove(f)}
                      disabled={busy}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
